/**
 * Reads the sheets of an Excel (.xlsx) file in the browser, without a library: an .xlsx is a zip of XML files, the
 * browser can unzip ("deflate-raw") and parse XML. Cells come back as text exactly as stored, so a phone number saved as
 * a number reads "9876543210", not "9.87654E+09". Dates and formulas' cached values come back as their stored text.
 */

type Entry = { name: string; method: number; compressed: number; offset: number };

function entries(buf: ArrayBuffer): Entry[] {
  const v = new DataView(buf);
  // End of central directory: the last record starting 0x06054b50
  let eocd = -1;
  for (let i = buf.byteLength - 22; i >= Math.max(0, buf.byteLength - 66000); i--) {
    if (v.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error("This doesn't look like an Excel (.xlsx) file");
  const count = v.getUint16(eocd + 10, true);
  let p = v.getUint32(eocd + 16, true);
  const out: Entry[] = [];
  const dec = new TextDecoder();
  for (let n = 0; n < count; n++) {
    if (v.getUint32(p, true) !== 0x02014b50) break;
    const method = v.getUint16(p + 10, true);
    const compressed = v.getUint32(p + 20, true);
    const nameLen = v.getUint16(p + 28, true);
    const extraLen = v.getUint16(p + 30, true);
    const commentLen = v.getUint16(p + 32, true);
    const offset = v.getUint32(p + 42, true);
    out.push({ name: dec.decode(new Uint8Array(buf, p + 46, nameLen)), method, compressed, offset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

async function readEntry(buf: ArrayBuffer, e: Entry): Promise<string> {
  const v = new DataView(buf);
  const start = e.offset + 30 + v.getUint16(e.offset + 26, true) + v.getUint16(e.offset + 28, true);
  const data = new Uint8Array(buf, start, e.compressed);
  if (e.method === 0) return new TextDecoder().decode(data);
  if (e.method !== 8 || typeof DecompressionStream === "undefined") throw new Error("This browser can't open Excel files; save the sheet as CSV instead");
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return await new Response(stream).text();
}

/** "B12" → column 1 (0-based) */
const colOf = (ref: string) => {
  const letters = (ref.match(/^[A-Z]+/) || ["A"])[0];
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
};

/** An opened .xlsx: its visible sheets in workbook order, and a reader for any of them */
export type XlsxBook = { sheets: string[]; read: (index: number) => Promise<string[][]> };

export async function openXlsx(file: File | Blob): Promise<XlsxBook> {
  const buf = await file.arrayBuffer();
  const list = entries(buf);
  const find = (name: string) => list.find((e) => e.name === name);
  const xml = (s: string) => new DOMParser().parseFromString(s, "application/xml");

  const shared: string[] = [];
  const ss = find("xl/sharedStrings.xml");
  if (ss) {
    xml(await readEntry(buf, ss)).querySelectorAll("si").forEach((si) => {
      shared.push(Array.from(si.getElementsByTagName("t")).map((t) => t.textContent || "").join(""));
    });
  }

  // Sheets in workbook order; hidden ones (lists a template keeps for its dropdowns) are not offered. Falls back to sheet1.xml.
  const sheets: Array<{ name: string; path: string }> = [];
  const wb = find("xl/workbook.xml");
  const rels = find("xl/_rels/workbook.xml.rels");
  if (wb && rels) {
    const targets = new Map(Array.from(xml(await readEntry(buf, rels)).getElementsByTagName("Relationship")).map((r) => [r.getAttribute("Id") || "", r.getAttribute("Target") || ""]));
    for (const sh of Array.from(xml(await readEntry(buf, wb)).getElementsByTagName("sheet"))) {
      if (["hidden", "veryHidden"].includes(sh.getAttribute("state") || "")) continue;
      const rid = sh.getAttribute("r:id") || sh.getAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id") || "";
      const target = targets.get(rid);
      if (target) sheets.push({ name: sh.getAttribute("name") || `Sheet ${sheets.length + 1}`, path: target.startsWith("/") ? target.slice(1) : `xl/${target.replace(/^\.\//, "")}` });
    }
  }
  if (!sheets.length) sheets.push({ name: "Sheet1", path: "xl/worksheets/sheet1.xml" });

  const read = async (index: number): Promise<string[][]> => {
    const entry = sheets[index] && find(sheets[index]!.path);
    if (!entry) throw new Error("The workbook has no sheet to read");
    const rows: string[][] = [];
    xml(await readEntry(buf, entry)).querySelectorAll("sheetData > row").forEach((rowEl) => {
      const row: string[] = [];
      rowEl.querySelectorAll("c").forEach((c) => {
        const i = colOf(c.getAttribute("r") || "A");
        const type = c.getAttribute("t");
        let val = "";
        if (type === "inlineStr") val = Array.from(c.getElementsByTagName("t")).map((t) => t.textContent || "").join("");
        else {
          const raw = c.getElementsByTagName("v")[0]?.textContent ?? "";
          val = type === "s" ? shared[Number(raw)] ?? "" : type === "b" ? (raw === "1" ? "TRUE" : "FALSE") : raw;
        }
        while (row.length < i) row.push("");
        row[i] = val;
      });
      rows.push(row);
    });
    return rows;
  };
  return { sheets: sheets.map((x) => x.name), read };
}
